import { connect } from 'react-redux';
import React, { useEffect } from 'react';
import {
    Label,
    Link,
    Menu,
    MenuButtonProps,
    MenuGroupHeader,
    MenuItem,
    MenuItemSwitch,
    MenuList,
    MenuPopover,
    MenuTrigger,
    SplitButton,
    MessageBar,
    MessageBarActions,
    MessageBarBody,
    MessageBarTitle,
    Spinner,
} from '@fluentui/react-components';

import {
    ArrowSyncRegular,
    ArrowSyncDismissRegular,
    ArrowClockwiseRegular,
    Checkmark16Regular,
    CheckmarkCircleRegular,
    OptionsRegular,
    TextBulletListSquareRegular,
    SyncOffRegular,
    ClockRegular,
    ClockAlarmRegular,
} from '@fluentui/react-icons';

// Component functions
import * as ComponentFunctions from './ConsumptionSyncProcess.Functions';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

// styles
import './ConsumptionSyncProcess.Styles.css';
import ProtocolConsumptionSyncProcessLogComponent from './ProtocolConsumptionSyncProcessLog/ProtocolConsumptionSyncProcessLog.Component';
import ConsumptionSyncProcessIAAnalysisComponent from './ConsumptionsSyncProcessIAAnalisys/ConsumptionSyncProcessIAAnalysis.Component';
import { IAAgentAvatar } from '../../../commons/component/IAAgentAvatar/IAAgentAvatar.Component';
import { SyncProcessLogSeverityEnum } from '../../../models/Enums/SyncProcessLogSeverityEnum';
import { SYNC_PROCESS_LOG_LEVELS, getSyncProcessLogLevel } from '../../../models/Entities/Protocols/SyncProcessLogLevel';
import { DEFAULT_MIN_LOG_SEVERITY } from '../../../services/ProtocolConsumptionSyncProcess.Service';
import HelpDocumentationLinkComponent from '../../HelpDocumentation/HelpDocumentationLink.Component';

/**
 * Código de enlace de la ayuda de facturables. Lo resuelve la API de documentación
 * contra la página que tenga configurada: acá sólo se nombra, así que el contenido
 * se puede reescribir o mudar de lugar sin tocar el front.
 *
 * Por ahora apunta a la página de prueba, que es el único enlace dado de alta. Al
 * crear el de facturables alcanza con cambiar este valor.
 */
const MEDICAL_PAYABLES_HELP_CODE = 'prueba.online';

type SwitchChangeData = {
    name: string;
    checkedItems: string[];
};

type StatusVariant = 'idle' | 'running' | 'success' | 'error' | 'stalled';

/**
 * Cada cuánto se recalcula el tiempo transcurrido mientras hay un proceso
 * corriendo. Marca el retraso con el que puede aparecer el reintento: con un
 * umbral de 2 minutos, un tick de 30 segundos lo demoraba hasta un 25% de más.
 */
const ELAPSED_TICK_MS = 15_000;

const ConsumptionSyncProcessComponent = (props: ComponentFunctions.Props) => {
    const { t } = useTranslation();

    const [useMedicalPayableDefinitions, setUseMedicalPayableDefinitions] = React.useState<boolean>(false);
    const [isLogDrawerOpen, setIsLogDrawerOpen] = React.useState<boolean>(false);
    const [isAnalysisDrawerOpen, setIsAnalysisDrawerOpen] = React.useState<boolean>(false);

    // Nivel de registro con el que se va a lanzar la próxima sincronización.
    // Arranca en el relajado, que es el default del backend.
    const [minLogSeverity, setMinLogSeverity] = React.useState<SyncProcessLogSeverityEnum>(DEFAULT_MIN_LOG_SEVERITY);

    useEffect(() => {
        if (props.protocol?.lastConsumptionSyncProcess === undefined) {
            props.GetLastProtocolConsumptionSyncProcessByProtocolId(props.ProtocolId);
        }
    }, [props.protocol?.id]);

    useEffect(() => {
        if (props.CurrentProcess) {
            setUseMedicalPayableDefinitions(props.CurrentProcess.useMedicalPayableDefinition);
        }
    }, [props.CurrentProcess]);

    // poll while running
    useEffect(() => {
        if (props.CurrentProcess?.status === 1 || props.CurrentProcess?.status === 2) {
            const interval = setInterval(() => {
                props.GetLastProtocolConsumptionSyncProcessByProtocolId(props.ProtocolId);
            }, 5000);
            return () => clearInterval(interval);
        }
    }, [props.CurrentProcess?.status]);

    useEffect(() => {
        if (props.successAdding === true && props.CurrentProcess?.status === 3) {
            props.GetAllProtocolConsumptionsByProtocol(props.ProtocolId, 1, 100, props.filters);
        }
    }, [props.successAdding && props.CurrentProcess?.status]);

    const menuCheckedValues = React.useMemo(
        () => ({ medicalPayableDefinitions: useMedicalPayableDefinitions ? ['medicalPayableDefinitionOption'] : [] }),
        [useMedicalPayableDefinitions]
    );

    const handleSwitchChange = React.useCallback((_, data: SwitchChangeData) => {
        if (data.name === 'medicalPayableDefinitions') {
            const nextValue = data.checkedItems.includes('medicalPayableDefinitionOption');
            setUseMedicalPayableDefinitions(nextValue);
        }
    }, []);

    const SyncProcessOptions = () => (
        <Menu checkedValues={menuCheckedValues} onCheckedValueChange={handleSwitchChange}>
            <MenuTrigger disableButtonEnhancement>
                <button
                    type="button"
                    className="csp-optionsBtn"
                    aria-label={t('consumptionSyncProcess.options.menuAria')}
                    title={t('consumptionSyncProcess.options.menuAria')}
                >
                    <OptionsRegular />
                </button>
            </MenuTrigger>
            <MenuPopover>
                <MenuList>
                    {/* El nivel de registro no vive acá sino en el desplegable del
                        botón de sincronizar: es una decisión de cada corrida, no
                        una preferencia, y tener dos controles del mismo ajuste es
                        lo que vuelve confusa una pantalla. */}
                    <MenuItemSwitch name="medicalPayableDefinitions" value="medicalPayableDefinitionOption">
                        <Label>{t('consumptionSyncProcess.options.enable')}</Label>
                        <p>{t('consumptionSyncProcess.options.description')}</p>
                    </MenuItemSwitch>
                </MenuList>
            </MenuPopover>
        </Menu>
    );

    // Derived state
    const status = props.CurrentProcess?.status;
    const isAdding = !!props.isAdding;
    const lastSync = props.CurrentProcess?.lastSync
        ? moment(props.CurrentProcess.lastSync).format('DD/MM/YYYY HH:mm')
        : undefined;

    const isRunningStatus = ComponentFunctions.isRunningSyncStatus(status);

    // Reloj propio: el tiempo transcurrido tiene que seguir avanzando aunque el
    // servidor deje de responder, que es justamente el caso que hay que detectar.
    const [now, setNow] = React.useState<number>(() => Date.now());

    useEffect(() => {
        if (!isRunningStatus) return;

        setNow(Date.now());
        const tick = setInterval(() => setNow(Date.now()), ELAPSED_TICK_MS);
        return () => clearInterval(tick);
    }, [isRunningStatus, props.CurrentProcess?.id]);

    // Respaldo para medir la duración cuando el proceso no trae `lastSync`:
    // el momento en que esta pantalla lo vio corriendo por primera vez. Es una
    // cota inferior —si la pantalla se abrió tarde, mide de menos—, así que
    // nunca marca como trabado un proceso que en realidad recién arrancó.
    const observedStartRef = React.useRef<{ processId?: string; at: number } | undefined>(undefined);

    useEffect(() => {
        if (!isRunningStatus) {
            observedStartRef.current = undefined;
            return;
        }

        if (observedStartRef.current?.processId !== props.CurrentProcess?.id) {
            observedStartRef.current = { processId: props.CurrentProcess?.id, at: Date.now() };
        }
    }, [isRunningStatus, props.CurrentProcess?.id]);

    const runningStartedAt = React.useMemo((): number | undefined => {
        if (!isRunningStatus) return undefined;

        const reported = props.CurrentProcess?.lastSync
            ? moment(props.CurrentProcess.lastSync).valueOf()
            : NaN;

        if (Number.isFinite(reported)) return reported;

        return observedStartRef.current?.at;
        // `now` entra a propósito: al primer tick ya existe el respaldo local que
        // el efecto de arriba escribe después del primer render.
    }, [isRunningStatus, props.CurrentProcess?.lastSync, now]);

    const { elapsedMinutes, isStalled, canRetry } = ComponentFunctions.evaluateSyncProgress({
        status,
        isAdding,
        failureAdding: !!props.failureAdding,
        startedAtMs: runningStartedAt,
        nowMs: now,
    });

    let variant: StatusVariant = 'idle';
    let icon: React.ReactNode = <SyncOffRegular />;
    let title: string = t('consumptionSyncProcess.status.idle');
    let eyebrow: string = t('consumptionSyncProcess.eyebrow.sync');
    let pillLabel: string = t('consumptionSyncProcess.pill.idle');
    let canSync = true;
    let actionDisabled = false;

    if (isAdding) {
        variant = 'running';
        icon = <Spinner size="extra-tiny" />;
        title = t('consumptionSyncProcess.status.generating');
        pillLabel = t('consumptionSyncProcess.pill.running');
        canSync = false;
        actionDisabled = true;
    } else if (isStalled) {
        // Se evalúa antes que 1 y 2 justamente porque el proceso sigue diciendo
        // que está en curso: es el servidor el que no avisa que se colgó.
        variant = 'stalled';
        icon = <ClockAlarmRegular />;
        eyebrow = t('consumptionSyncProcess.eyebrow.stalled');
        title = t('consumptionSyncProcess.status.stalled', { minutes: elapsedMinutes });
        pillLabel = t('consumptionSyncProcess.pill.stalled');
    } else if (status === 1) {
        variant = 'running';
        icon = <ArrowSyncRegular />;
        title = t('consumptionSyncProcess.status.syncing');
        pillLabel = t('consumptionSyncProcess.pill.running');
        canSync = false;
        actionDisabled = true;
    } else if (status === 2) {
        variant = 'running';
        icon = <ArrowSyncRegular />;
        title = t('consumptionSyncProcess.status.processing');
        pillLabel = t('consumptionSyncProcess.pill.running');
        canSync = false;
        actionDisabled = true;
    } else if (status === 3) {
        variant = 'success';
        icon = <CheckmarkCircleRegular />;
        // the eyebrow already says "última sincronización", so the title carries
        // just the date instead of repeating the label
        eyebrow = t('consumptionSyncProcess.eyebrow.lastSync');
        title = lastSync
            ? t('consumptionSyncProcess.status.successAt', { date: lastSync })
            : t('consumptionSyncProcess.status.successNoDate');
        pillLabel = t('consumptionSyncProcess.pill.success');
    } else if (status === 5) {
        variant = 'error';
        icon = <ArrowSyncDismissRegular />;
        eyebrow = t('consumptionSyncProcess.eyebrow.error');
        title = lastSync
            ? t('consumptionSyncProcess.status.failedAt', { date: lastSync })
            : t('consumptionSyncProcess.status.failed');
        pillLabel = t('consumptionSyncProcess.pill.error');
    }

    // Cuando hay algo para reintentar el botón lo dice: "Sincronizar" al lado de
    // un error se lee como empezar de cero, no como volver a intentar lo mismo.
    const actionLabel = canRetry
        ? t('consumptionSyncProcess.actions.retry')
        : t('consumptionSyncProcess.actions.sync');

    const handleSync = () =>
        props.CreateProtocolConsumptionSyncProcess(props.ProtocolId, useMedicalPayableDefinitions, minLogSeverity);

    // El nivel se pasa por parámetro y no desde el estado: setState no es
    // inmediato y leerlo acá lanzaría la corrida con el nivel anterior.
    const handleSyncWithLevel = (severity: SyncProcessLogSeverityEnum) => {
        setMinLogSeverity(severity);
        props.CreateProtocolConsumptionSyncProcess(props.ProtocolId, useMedicalPayableDefinitions, severity);
    };

    // Nivel con el que se lanzará la próxima corrida: se muestra junto al botón
    // porque condiciona lo que después va a haber para mirar en el log.
    const selectedLogLevel = getSyncProcessLogLevel(minLogSeverity);

    return (
        <>
            {props.haveContractAssociated && (
                <div className="csp-container">
                    <div className={`csp-card is-${variant}`}>
                        <div className="csp-iconChip">{icon}</div>
                        <div className="csp-textBlock">
                            <span className="csp-eyebrow">{eyebrow}</span>
                            <span className="csp-title" title={title}>{title}</span>
                            <div className="csp-meta">
                                <span className={`csp-pill csp-pill--${variant}`}>{pillLabel}</span>
                                {lastSync && (variant === 'running' || variant === 'stalled') && (
                                    <span className="csp-timestamp">
                                        <ClockRegular style={{ verticalAlign: 'middle', marginRight: 4 }} />
                                        {lastSync}
                                    </span>
                                )}
                                {/* El tiempo corriendo se muestra desde el arranque y no sólo al
                                    trabarse: así se ve venir el problema en vez de descubrirlo
                                    cuando ya pasaron diez minutos. */}
                                {elapsedMinutes !== undefined && (variant === 'running' || variant === 'stalled') && (
                                    <span className="csp-timestamp">
                                        {t('consumptionSyncProcess.elapsed', { minutes: elapsedMinutes })}
                                    </span>
                                )}
                                {/* El nivel elegido se anuncia antes de sincronizar:
                                    es lo que decide cuánto detalle va a quedar
                                    registrado, y después no se puede cambiar. */}
                                {canSync && selectedLogLevel && (
                                    <span className="csp-logLevel" title={t(selectedLogLevel.descriptionKey)}>
                                        {t('consumptionSyncProcess.logLevel.badge', {
                                            level: t(selectedLogLevel.labelKey),
                                        })}
                                    </span>
                                )}
                            </div>
                        </div>
                        {(canSync || !!props.CurrentProcess?.id) && (
                            <div className="csp-actions">
                                {!!props.CurrentProcess?.id && (
                                    <button
                                        type="button"
                                        className="csp-secondaryBtn"
                                        onClick={() => setIsLogDrawerOpen(true)}
                                    >
                                        <span className="csp-btn-iconWrap">
                                            <TextBulletListSquareRegular />
                                        </span>
                                        <span>{t('consumptionSyncProcess.actions.logs', 'Ver logs')}</span>
                                    </button>
                                )}
                                {/* El análisis es una lectura sobre la corrida ya hecha, así
                                    que acompaña al botón de logs en vez de competir con la
                                    acción principal de sincronizar. */}
                                {!!props.CurrentProcess?.id && (
                                    <button
                                        type="button"
                                        className="csp-secondaryBtn"
                                        onClick={() => setIsAnalysisDrawerOpen(true)}
                                    >
                                        <span className="csp-btn-iconWrap">
                                            <IAAgentAvatar size={18} />
                                        </span>
                                        <span>{t('consumptionSyncProcess.actions.analysis', 'Ver análisis')}</span>
                                    </button>
                                )}
                                {/* La acción principal sincroniza con el nivel elegido;
                                    el desplegable lanza directamente con otro. El nivel
                                    se decide al correr, que es cuando importa: después
                                    no se puede recuperar lo que no se registró. */}
                                <Menu positioning="below-end">
                                    <MenuTrigger disableButtonEnhancement>
                                        {(triggerProps: MenuButtonProps) => (
                                            <SplitButton
                                                className={`csp-splitBtn csp-splitBtn--${variant}`}
                                                appearance="primary"
                                                disabled={actionDisabled}
                                                menuButton={{
                                                    ...triggerProps,
                                                    'aria-label': t('consumptionSyncProcess.logLevel.chooseAria', 'Elegir nivel de registro'),
                                                    title: t('consumptionSyncProcess.logLevel.chooseAria', 'Elegir nivel de registro'),
                                                }}
                                                primaryActionButton={{ onClick: handleSync }}
                                                icon={actionDisabled
                                                    ? <Spinner size="extra-tiny" />
                                                    : canRetry ? <ArrowClockwiseRegular /> : <ArrowSyncRegular />}
                                            >
                                                {actionLabel}
                                            </SplitButton>
                                        )}
                                    </MenuTrigger>
                                    <MenuPopover>
                                        <MenuList>
                                            <MenuGroupHeader>
                                                {t('consumptionSyncProcess.logLevel.runWith', 'Sincronizar con nivel de registro')}
                                            </MenuGroupHeader>
                                            {SYNC_PROCESS_LOG_LEVELS.map((level) => (
                                                <MenuItem
                                                    key={level.severity}
                                                    icon={level.severity === minLogSeverity ? <Checkmark16Regular /> : <span />}
                                                    onClick={() => handleSyncWithLevel(level.severity)}
                                                >
                                                    <Label>{t(level.labelKey)}</Label>
                                                    <p>{t(level.descriptionKey)}</p>
                                                </MenuItem>
                                            ))}
                                        </MenuList>
                                    </MenuPopover>
                                </Menu>
                                {canSync && <SyncProcessOptions />}
                            </div>
                        )}
                    </div>

                    {/* Se explica por qué apareció el reintento sobre un proceso que
                        el servidor sigue reportando como "en curso". */}
                    {isStalled && (
                        <MessageBar intent={'warning'}>
                            <MessageBarBody>
                                <MessageBarTitle>{t('consumptionSyncProcess.stalled.title')}</MessageBarTitle>
                                {t('consumptionSyncProcess.stalled.description', {
                                    minutes: elapsedMinutes,
                                    threshold: ComponentFunctions.STALL_THRESHOLD_MINUTES,
                                })}
                            </MessageBarBody>
                        </MessageBar>
                    )}

                    {!useMedicalPayableDefinitions && (
                        <MessageBar intent={'warning'}>
                            <MessageBarBody>
                                <MessageBarTitle>{t('consumptionSyncProcess.warning.title')}</MessageBarTitle>
                                {t('consumptionSyncProcess.warning.disabled')}
                            </MessageBarBody>
                            {/* La explicación larga —qué son los facturables y qué
                                implica tenerlos deshabilitados— vive en la ayuda, no
                                en el aviso: acá entra una línea. */}
                            <MessageBarActions>
                                <HelpDocumentationLinkComponent
                                    linkCode={MEDICAL_PAYABLES_HELP_CODE}
                                    drawerTitle={t('consumptionSyncProcess.warning.title')}
                                    label='¿Porqué aparece este mensaje?'            
                                />
                            </MessageBarActions>
                        </MessageBar>
                    )}
                </div>
            )}

            {props.isLoading && !props.haveContractAssociated && (
                <MessageBar intent={'warning'}>
                    <MessageBarBody>
                        <MessageBarTitle>{t('consumptionSyncProcess.noContract.title')}</MessageBarTitle>
                        {t('consumptionSyncProcess.noContract.description')}{' '}
                        <Link href={'/contracts'}>{t('consumptionSyncProcess.noContract.link')}</Link>
                    </MessageBarBody>
                </MessageBar>
            )}

            {(props.failureLoading || props.failureAdding) && (
                <MessageBar intent={'error'}>
                    <MessageBarBody>
                        <MessageBarTitle>{t('consumptionSyncProcess.error.title')}</MessageBarTitle>
                        {props.ErrorAdding?.ErrorMessage || props.ErrorGetting?.ErrorMessage}
                    </MessageBarBody>
                </MessageBar>
            )}

            {props.CurrentProcess?.id && (
                <ProtocolConsumptionSyncProcessLogComponent
                    ProtocolId={props.ProtocolId}
                    SyncProcessId={props.CurrentProcess.id}
                    MinLogSeverity={props.CurrentProcess.minLogSeverity}
                    isOpen={isLogDrawerOpen}
                    onDismiss={() => setIsLogDrawerOpen(false)}
                />
            )}

            {props.CurrentProcess?.id && (
                <ConsumptionSyncProcessIAAnalysisComponent
                    SyncProcessId={props.CurrentProcess.id}
                    ProtocolId={props.ProtocolId}
                    SyncProcessFinished={ComponentFunctions.isTerminalSyncStatus(props.CurrentProcess.status)}
                    isOpen={isAnalysisDrawerOpen}
                    onDismiss={() => setIsAnalysisDrawerOpen(false)}
                />
            )}
        </>
    );
};

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(ConsumptionSyncProcessComponent as any);
